import { User } from "firebase/auth";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { joinLeague, lookupLeagueByCode, LeagueLookupResult } from "../api/leagues";
import { ApiError } from "../api/client";
import { card, widePageContainer, primaryBtn, secondaryBtn, inputStyle, errorStyle } from "../styles";
import { MODE_CONTENT } from "./createLeague/modeContent";

interface Props {
  user: User;
}

const STEP_COLORS = ["var(--color-mode-syndicate)", "var(--color-mode-score-predictor)", "var(--color-mode-final-positions)"];

export default function JoinLeaguePage({ user }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  useBreadcrumbs([{ label: t("common.home"), to: "/overview" }, { label: t("joinLeague.title") }]);

  const [inviteCode, setInviteCode] = useState("");
  const [lookupError, setLookupError] = useState("");
  const [looking, setLooking] = useState(false);
  const [preview, setPreview] = useState<LeagueLookupResult | null>(null);

  const [joinError, setJoinError] = useState("");
  const [joining, setJoining] = useState(false);

  const steps = [t("joinLeague.steps.one"), t("joinLeague.steps.two"), t("joinLeague.steps.three")];

  const handleLookup = async () => {
    if (!inviteCode.trim()) { setLookupError(t("joinLeague.errors.codeRequired")); return; }
    setLookupError("");
    setPreview(null);
    setLooking(true);
    try {
      const data = await lookupLeagueByCode(user, inviteCode.trim().toUpperCase());
      setPreview(data);
    } catch (err) {
      if (err instanceof ApiError) {
        setLookupError(err.status === 404 ? t("joinLeague.errors.notFound") : err.message);
      } else {
        setLookupError(t("joinLeague.errors.lookupFailed"));
      }
    } finally {
      setLooking(false);
    }
  };

  const handleJoin = async () => {
    setJoinError("");
    setJoining(true);
    try {
      const data = await joinLeague(user, inviteCode.trim().toUpperCase());
      navigate(`/leagues/${data.id}`, { state: { justJoined: true } });
    } catch (err) {
      setJoinError(err instanceof ApiError ? err.message : t("joinLeague.errors.failed"));
    } finally {
      setJoining(false);
    }
  };

  const Icon = preview ? MODE_CONTENT[preview.mode].icon : null;

  return (
    <div className="sg-fade-in" style={{ ...widePageContainer, padding: "1.5rem 0" }}>

      {/* ── Hero ── */}
      <section style={{ marginBottom: "2.5rem" }}>
        <div style={{
          borderRadius: 20,
          padding: isMobile ? "2rem 1.5rem" : "3rem 3.5rem",
          background: "var(--color-surface-alt)",
          border: "1px solid var(--color-border-subtle)",
        }}>
          <div style={{
            fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.12em", color: "var(--color-text-dim)", marginBottom: "0.9rem",
          }}>
            {t("joinLeague.title")}
          </div>
          <h1 style={{
            margin: "0 0 1.1rem",
            fontFamily: "var(--font-display)",
            fontSize: isMobile ? "1.9rem" : "2.6rem",
            fontWeight: 800,
            color: "var(--color-text)",
            lineHeight: 1.15,
          }}>
            {t("joinLeague.heading")}
          </h1>
          <p style={{ margin: 0, fontSize: "1rem", color: "var(--color-text-muted)", lineHeight: 1.7, maxWidth: 620 }}>
            {t("joinLeague.subtitle")}
          </p>
        </div>
      </section>

      {/* ── 3-step instructions ── */}
      <section style={{ marginBottom: "2.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "0.75rem" }}>
          {steps.map((body, i) => (
            <div key={i} style={{ ...card, display: "flex", alignItems: "flex-start", gap: "0.85rem" }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "0.8rem", fontWeight: 700,
                background: STEP_COLORS[i], color: "var(--color-btn-primary-text)",
              }}>
                {i + 1}
              </div>
              <p style={{ margin: 0, fontSize: "0.88rem", color: "var(--color-text-muted)", lineHeight: 1.5 }}>
                {body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Code entry + preview ── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "1.25rem", alignItems: "start" }}>
        <div style={card}>
          <label htmlFor="invite-code" style={{
            display: "block", fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.12em", color: "var(--color-text-dim)", marginBottom: "0.6rem",
          }}>
            {t("joinLeague.codeLabel")}
          </label>
          <input
            id="invite-code"
            type="text"
            placeholder={t("joinLeague.codePlaceholder")}
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handleLookup()}
            style={{ ...inputStyle, marginBottom: "0.75rem" }}
            autoFocus
          />
          {lookupError && <p style={errorStyle}>{lookupError}</p>}
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button onClick={handleLookup} style={primaryBtn} disabled={looking || !inviteCode.trim()}>
              {looking ? t("common.saving") : t("joinLeague.findLeague")}
            </button>
            <Link to="/overview" style={{ ...secondaryBtn, textDecoration: "none" }}>
              {t("joinLeague.cancel")}
            </Link>
          </div>
        </div>

        {preview && (
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.85rem" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--color-success)" }} />
              <span style={{
                fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
                letterSpacing: "0.12em", color: "var(--color-text-dim)",
              }}>
                {t("joinLeague.codeRecognised")}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.25rem" }}>
              {Icon && (
                <div style={{
                  width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "var(--color-surface-alt)", color: "var(--color-accent)",
                }}>
                  <Icon />
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "var(--color-text)" }}>
                  {preview.name}
                </div>
                <div style={{ fontSize: "0.78rem", color: "var(--color-text-muted)" }}>
                  {t(`createLeague.mode.${preview.mode}.label`)} · {t("joinLeague.membersLabel", { count: preview.memberCount })} · {preview.competition.name} {preview.competition.season}
                </div>
              </div>
            </div>

            {preview.alreadyMember ? (
              <>
                <p style={{ margin: "0 0 0.75rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  {t("joinLeague.alreadyMember.title")}
                </p>
                <Link to={`/leagues/${preview.id}`} style={{ ...primaryBtn, textDecoration: "none", display: "inline-block" }}>
                  {t("joinLeague.alreadyMember.cta")} →
                </Link>
              </>
            ) : !preview.joinable ? (
              // A league can no longer be joined once it has started — no VIEWER fallback,
              // no Join button, just the reason. Mirrors alreadyMember's info-only shape.
              <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                {t("joinLeague.notJoinable")}
              </p>
            ) : (
              <>
                {joinError && <p style={errorStyle}>{joinError}</p>}
                <button
                  onClick={handleJoin}
                  disabled={joining}
                  style={{ ...primaryBtn, width: "100%" }}
                >
                  {joining ? t("common.saving") : `${t("joinLeague.joinCta", { name: preview.name })} →`}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
